import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useToast } from "@/components/ui/use-toast";

interface AddSupplySheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSupplyAdded?: () => void;
}

export function AddSupplySheet({ isOpen, onClose, onSupplyAdded }: AddSupplySheetProps) {
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState("");
  const [showCustomUnit, setShowCustomUnit] = useState(false);
  const [customUnit, setCustomUnit] = useState("");
  const [formData, setFormData] = useState({
    itemName: "",
    category: "",
    brand: "",
    unit: "",
    costPerUnit: "",
    vendor: "",
  });
  const { toast } = useToast();

  const createSupply = useMutation(api.supplies.create);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check required fields including custom unit if selected
    const finalUnit = showCustomUnit ? customUnit : formData.unit;

    if (!formData.itemName || !finalUnit) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      // Use the actual category if custom is selected
      const finalCategory = showCustomCategory ? customCategory : formData.category;

      await createSupply({
        itemName: formData.itemName,
        category: finalCategory || undefined,
        brand: formData.brand || undefined,
        unit: finalUnit,
        costPerUnit: formData.costPerUnit ? parseFloat(formData.costPerUnit) : undefined,
        vendor: formData.vendor || undefined,
      });

      toast({
        title: "Success!",
        description: "Supply added successfully",
      });
      setFormData({
        itemName: "",
        category: "",
        brand: "",
        unit: "",
        costPerUnit: "",
        vendor: "",
      });
      setCustomCategory("");
      setShowCustomCategory(false);
      setCustomUnit("");
      setShowCustomUnit(false);
      onSupplyAdded?.();
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add supply",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Add Supply</SheetTitle>
          <SheetDescription>
            Add a new supply item to your organization's master list.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <form onSubmit={handleSubmit} className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="itemName">
                Item Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="itemName"
                value={formData.itemName}
                onChange={(e) => handleInputChange("itemName", e.target.value)}
                placeholder="Enter item name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => {
                  setShowCustomCategory(value === "custom");
                  handleInputChange("category", value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cleaning-chemicals">Cleaning Chemicals</SelectItem>
                  <SelectItem value="paper-products">Paper Products</SelectItem>
                  <SelectItem value="tools-equipment">Tools & Equipment</SelectItem>
                  <SelectItem value="safety-supplies">Safety Supplies</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="office-supplies">Office Supplies</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {showCustomCategory && (
              <div className="space-y-2">
                <Label htmlFor="custom-category">Custom Category</Label>
                <Input
                  id="custom-category"
                  placeholder="Enter custom category"
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="brand">Brand</Label>
              <Input
                id="brand"
                value={formData.brand}
                onChange={(e) => handleInputChange("brand", e.target.value)}
                placeholder="Enter brand name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit">
                Unit <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.unit}
                onValueChange={(value) => {
                  setShowCustomUnit(value === "custom");
                  handleInputChange("unit", value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ea">Each (ea)</SelectItem>
                  <SelectItem value="box">Box</SelectItem>
                  <SelectItem value="case">Case</SelectItem>
                  <SelectItem value="gal">Gallon (gal)</SelectItem>
                  <SelectItem value="L">Liter (L)</SelectItem>
                  <SelectItem value="oz">Ounce (oz)</SelectItem>
                  <SelectItem value="lb">Pound (lb)</SelectItem>
                  <SelectItem value="roll">Roll</SelectItem>
                  <SelectItem value="pack">Pack</SelectItem>
                  <SelectItem value="bag">Bag</SelectItem>
                  <SelectItem value="bottle">Bottle</SelectItem>
                  <SelectItem value="can">Can</SelectItem>
                  <SelectItem value="carton">Carton</SelectItem>
                  <SelectItem value="doz">Dozen (doz)</SelectItem>
                  <SelectItem value="pallet">Pallet</SelectItem>
                  <SelectItem value="qt">Quart (qt)</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {showCustomUnit && (
              <div className="space-y-2">
                <Label htmlFor="custom-unit">Custom Unit</Label>
                <Input
                  id="custom-unit"
                  placeholder="Enter custom unit"
                  value={customUnit}
                  onChange={(e) => setCustomUnit(e.target.value)}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="costPerUnit">Cost Per Unit</Label>
              <Input
                id="costPerUnit"
                type="number"
                step="0.01"
                value={formData.costPerUnit}
                onChange={(e) => handleInputChange("costPerUnit", e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="vendor">Vendor</Label>
              <Input
                id="vendor"
                value={formData.vendor}
                onChange={(e) => handleInputChange("vendor", e.target.value)}
                placeholder="Enter vendor name"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                Add Supply
              </Button>
            </div>
          </form>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
